import { useState } from 'react';
import PageTitle from '../components/common/PageTitle';
import InterventionList from '../components/sections/interventions/InterventionList';
import InterventionDetail from '../components/sections/interventions/InterventionDetail';
import NewsDetail from '../components/sections/interventions/NewsDetail';
import ContentFilters from '../components/sections/interventions/ContentFilters';
import { mockInterventions, mockNews } from '../data/mockData';

// Types
import { ContentType, InterventionCategory, NewsCategory } from '../types/contentTypes';

const InterventionsPage = () => {
  // State for filters and search
  const [contentType, setContentType] = useState<ContentType>('interventions');
  const [interventionCategory, setInterventionCategory] = useState<InterventionCategory>('all');
  const [newsCategory, setNewsCategory] = useState<NewsCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Sample data 
  const interventions = mockInterventions;
  const news = mockNews;

  // Filter and search logic
  const filteredInterventions = interventions.filter(item => {
    // Filter by category
    if (interventionCategory !== 'all' && item.category !== interventionCategory) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(query) ||
        item.excerpt.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        item.location.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const filteredNews = news.filter(item => {
    // Filter by category
    if (newsCategory !== 'all' && item.category !== newsCategory) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(query) ||
        item.excerpt.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Combine and sort items if 'all' content type is selected
  const allItems = contentType === 'all'
    ? [
        ...filteredInterventions.map(item => ({ ...item, type: 'intervention' as const })),
        ...filteredNews.map(item => ({ ...item, type: 'news' as const }))
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    : [];

  // Get selected item details
  const selectedIntervention = selectedItem && selectedItem.startsWith('int-')
    ? interventions.find(item => item.id === selectedItem)
    : null;

  const selectedNewsItem = selectedItem && selectedItem.startsWith('news-')
    ? news.find(item => item.id === selectedItem)
    : null;

  return (
    <>
      <PageTitle 
        title="Intervencije in novice" 
        subtitle="Pregled naših zadnjih intervencij in aktualnih novic"
        backgroundImage="/images/interventions-hero.jpg"
      />

      <section className="page-section">
        <div className="container-custom">
          {selectedItem ? (
            // Detail view
            <>
              {selectedIntervention && <InterventionDetail intervention={selectedIntervention} onBack={() => setSelectedItem(null)} />}
              {selectedNewsItem && <NewsDetail news={selectedNewsItem} onBack={() => setSelectedItem(null)} />}
            </>
          ) : (
            // List view
            <>
              <ContentFilters 
                contentType={contentType}
                setContentType={setContentType}
                interventionCategory={interventionCategory}
                setInterventionCategory={setInterventionCategory}
                newsCategory={newsCategory}
                setNewsCategory={setNewsCategory}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />

              <InterventionList 
                contentType={contentType} 
                filteredInterventions={filteredInterventions} 
                filteredNews={filteredNews} 
                allItems={allItems} 
                onItemSelect={setSelectedItem} 
              />
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default InterventionsPage;